/**
 * VersionCheckAgent
 * 
 * Agent responsible for polling and comparing versions.
 * 
 * Skills:
 * - POLL_VERSION: Set up interval polling
 * - COMPARE_VERSIONS: Compare stored vs fetched version
 * - STORE_VERSION: Save version to localStorage
 * - DETECT_CHANGE: Determine if update is available
 */

import VersionAPIAgent from './VersionAPIAgent';

const STORAGE_KEY = 'startnclex_app_version';
const DEFAULT_POLL_INTERVAL = 5 * 60 * 1000; // 5 minutes
const MOBILE_POLL_INTERVAL = 10 * 60 * 1000; // 10 minutes on mobile (battery saving)

const VersionCheckAgent = {
  name: 'VersionCheckAgent',
  
  // Internal state
  _intervalId: null,
  _callbacks: [],
  
  skills: {
    /**
     * STORE_VERSION
     * Persists version to localStorage
     * @param {string} version - Version string to store
     * @returns {boolean} Success status
     */
    STORE_VERSION: (version) => {
      try {
        localStorage.setItem(STORAGE_KEY, version);
        return true;
      } catch (error) {
        console.warn('[VersionCheckAgent] STORE_VERSION failed:', error.message);
        return false;
      }
    },
    
    /**
     * GET_STORED_VERSION
     * Retrieves stored version from localStorage
     * @returns {string|null} Stored version or null
     */
    GET_STORED_VERSION: () => {
      try {
        return localStorage.getItem(STORAGE_KEY);
      } catch (error) {
        return null;
      }
    },
    
    /**
     * COMPARE_VERSIONS
     * Compares two version strings
     * @param {string} stored - Stored version
     * @param {string} fetched - Fetched version
     * @returns {{changed: boolean, isNewer: boolean}}
     */
    COMPARE_VERSIONS: (stored, fetched) => {
      if (!stored || !fetched) {
        return { changed: false, isNewer: false };
      }
      
      const changed = stored !== fetched;
      // If changed and we had a previous version, it's newer
      const isNewer = changed && stored !== null;
      
      return { changed, isNewer };
    },
    
    /**
     * DETECT_CHANGE
     * Orchestrates fetch, compare, and store to detect updates
     * @returns {Promise<{updateAvailable: boolean, newVersion: string|null, currentVersion: string|null}>}
     */
    DETECT_CHANGE: async () => {
      const storedVersion = VersionCheckAgent.skills.GET_STORED_VERSION();
      
      // Fetch current version from API
      const apiResponse = await VersionAPIAgent.execute('GET_VERSION');
      const fetchedVersion = VersionAPIAgent.skills.RETURN_SHA(apiResponse);
      
      if (!fetchedVersion) {
        return {
          updateAvailable: false,
          newVersion: null,
          currentVersion: storedVersion
        };
      }
      
      // First time - just store the version
      if (!storedVersion) {
        VersionCheckAgent.skills.STORE_VERSION(fetchedVersion);
        return {
          updateAvailable: false,
          newVersion: null,
          currentVersion: fetchedVersion
        };
      }
      
      // Compare versions
      const { changed, isNewer } = VersionCheckAgent.skills.COMPARE_VERSIONS(
        storedVersion,
        fetchedVersion
      );
      
      return {
        updateAvailable: isNewer,
        newVersion: isNewer ? fetchedVersion : null,
        currentVersion: storedVersion
      };
    },
    
    /**
     * POLL_VERSION
     * Sets up recurring version checks
     * @param {object} options - Polling options
     * @param {number} options.interval - Poll interval in ms
     * @param {boolean} options.immediate - Run immediately
     * @param {function} options.onChange - Callback when update detected
     * @returns {number} Interval ID
     */
    POLL_VERSION: (options = {}) => {
      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        typeof navigator !== 'undefined' ? navigator.userAgent : ''
      );
      
      const interval = options.interval || (isMobile ? MOBILE_POLL_INTERVAL : DEFAULT_POLL_INTERVAL);
      const immediate = options.immediate !== false;
      const onChange = options.onChange || (() => {});
      
      // Clear existing interval
      if (VersionCheckAgent._intervalId) {
        clearInterval(VersionCheckAgent._intervalId);
      }
      
      // Store callback
      VersionCheckAgent._callbacks.push(onChange);
      
      const check = async () => {
        const result = await VersionCheckAgent.skills.DETECT_CHANGE();
        if (result.updateAvailable) {
          VersionCheckAgent._callbacks.forEach(cb => cb(result));
        }
      };
      
      // Run immediately if requested
      if (immediate) {
        check();
      }
      
      // Set up polling
      VersionCheckAgent._intervalId = setInterval(check, interval);
      
      return VersionCheckAgent._intervalId;
    },
    
    /**
     * STOP_POLLING
     * Stops the version polling
     */
    STOP_POLLING: () => {
      if (VersionCheckAgent._intervalId) {
        clearInterval(VersionCheckAgent._intervalId);
        VersionCheckAgent._intervalId = null;
      }
      VersionCheckAgent._callbacks = [];
    }
  },
  
  /**
   * Execute a skill by name
   */
  execute: async (skillName, ...args) => {
    const skill = VersionCheckAgent.skills[skillName];
    if (!skill) {
      throw new Error(`[VersionCheckAgent] Unknown skill: ${skillName}`);
    }
    return await skill(...args);
  }
};

export default VersionCheckAgent;
